






























import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { RawLocation, Route } from "vue-router";
import { DomainRegistry } from "../../domain";
import { BrowserUtility } from "../../utilities";
import { IAuthenticationDialogCondition } from "./dialogs";
import { AuthenticationMixin } from "./mixins";

// 通常の認証(requiresAuth指定によるグローバルガードでのリダイレクト)で対応できない後認証に対応するログイン画面.
// 後認証対応が必要ないユースケースでは使わない事.
@Component
export default class UnmanagedLoginView extends Mixins(AuthenticationMixin) {
  @Prop()
  departmentCode: string;

  @Prop()
  hostName: string;

  @Prop()
  shouldAuthenticateLater: boolean;

  @Prop()
  doAuthentication: (userId: string, password: string) => Promise<void>;

  @Prop()
  beforeEnter: (to: Route) => Promise<RawLocation>;

  @Prop()
  // Configurationで同名の変数を使用している為、Viewから受け取るパラメータのプロパティ名を変更している
  shouldSelectDepartmentManually: boolean;

  $refs!: {
    password: HTMLInputElement,
  };

  data(): any {
    return {
      departmentId: this.departmentCode,
      isLoginProcessed: false,
      isAlreadyAuthorized: false,
    };
  }
  departmentId: string;
  isLoginProcessed: boolean;    // ログイン処理済か.既ログイン/自動ログイン/手動ログインに関わらずログインしてたらtrueに.
  isAlreadyAuthorized: boolean; // 認証済か.手動ログインで後認証(shouldAuthenticateLater=true)の場合はログイン済でもfalseのまま.

  get isProcessing(): boolean {
    return this.isAuthenticating;
  }

  get isCloseEnable(): boolean {
    return this.$configuration.behavior.closer.isEnable && this.$store.getters.isManualCloseable !== false;
  }

  get shouldSelectDepartment(): boolean {
    // パラメータで同名の変数を使用しており、パラメータ側の変数名をshouldSelectDepartmentManuallyへ変更している
    return this.$configuration.behavior.authentication.login.shouldSelectDepartment;
  }

  get loginIdInputType(): string {
    // モバイル版のchromeでtelを指定すると電話番号入力専用のIMEが起動されるためアルファベット入力できない.(iOSのSafariは大丈夫.)textにする.
    return (BrowserUtility.isChromiumBrowser() && BrowserUtility.isMobile())
      ? "text"
      : "tel";
  }

  onIdEntered(): void {
    if (this.shouldAuthenticateLater) {
      this._login();
    } else {
      this.focusToPassword();
    }
  }

  onPasswordEntered(): void {
    this._login();
  }

  onLoginClicked(): void {
    this._login();
  }

  async created(): Promise<void> {
    if (this.isAlreadyLogined()) {
      await this.handleAuthenticationSucceeded();
      this.isAlreadyAuthorized = !this.id && !this.shouldAuthenticateLater;  // ページリロード時は既ログインになる.手動ログインで後認証じゃない場合はtrueに.
    } else if (this.id) {
      await this._loginAutomatically();
    }
  }

  isAlreadyLogined(): boolean { // 算出プロパティとして状態持ってると他と区別がややこしいのでメソッドにしておく.
    // 違うユーザ/診療科が指定されてる場合はログイン未として扱う.
    return this.$store.getters.token
        && (!this.userCode || (this.$store.getters.user && (this.$store.getters.user.id === this.userCode)))
        && (!this.departmentCode || (this.$store.getters.department && (this.$store.getters.department.id === this.departmentCode)));
  }

  get loginedId(): string {
    return this.$store.getters.user
      ? this.$store.getters.user.id
      : "";
  }

  async authenticate(isForcibly: boolean): Promise<boolean> {
    return (!isForcibly && this.isAlreadyAuthorized)
        || this._authenticate();
  }

  private async _authenticate(): Promise<boolean> {
    try {
      this.$logger.info("認証を開始します。");
      await this.$commonDialogs.authentication.authenticator.selectOptionConditionally(this.condition);
      return true;
    } catch (e) {
      // 例外来るのはキャンセル時.(認証失敗はダイアログ内でエラー出して入力継続.)
      return false;
    }
  }

  get condition(): IAuthenticationDialogCondition {
    return {
        userCode: this.loginedId,
        doAuthentication: this.doAuthentication,
    };
  }

  private async _loginAutomatically(): Promise<void> {
    try {
      this.$logger.info("自動ログイン処理を開始します。");
      await this.loginAutomatically(this.id, this.departmentId, this.hostName, this.orderUserId, this.attendingUserId, null, null);
      this.isAlreadyAuthorized = !this.shouldAuthenticateLater;
      await this.handleAuthenticationSucceeded();
    } catch (e) {
      this.handleAuthenticationFailed(
        e,
        "自動ログイン失敗",
        (e && e.response && e.response.status === 504)
          ? "サーバーとの通信に失敗しました。"
          : "自動ログイン失敗しました。" + this.toExceptionMessage(e),
      );
    }
  }

  private async _login(): Promise<void> {
    if (this.isAuthenticating) { return; }
    try {
      this.$logger.info("ログイン処理を開始します。");
      if (this.shouldAuthenticateLater) {
        await this.loginAutomatically(this.id, this.departmentId, this.hostName, this.orderUserId, this.attendingUserId, null, null);
        // 手動ログインで後認証の場合はisAlreadyAuthorizedはtrueにしない.
      } else {
        await this.login(this.id, this.password, this.departmentId, this.hostName, null, null);
        this.isAlreadyAuthorized = true;
      }
      this.$success(
        "ログイン成功",
        "ログインしました。",
      );
      (this.$el as HTMLElement).focus(); // IEで入力項目にフォーカスが当たったまま遷移した場合、上記カードが消えるまで押せなくなる問題の対応
      await this.handleAuthenticationSucceeded();
    } catch (e) {
      this.handleAuthenticationFailed(
        e,
        "ログイン失敗",
        "ログイン失敗しました。ログインIDとパスワードを確認してください。",
      );
    }
  }

  private async handleAuthenticationSucceeded(): Promise<void> {
    if (this.shouldSelectDepartment) {
      await this.selectLoginDepartment();
    }
    this.isLoginProcessed = true;
    await this.redirect();
  }

  private async redirect(): Promise<void> {
    const location = this.beforeEnter
      ? await this.beforeEnter(this.$route)
      : null;
    if (location) {
      this.$router.replace(location);
    } else {
      const path = this.trimLoginInformation(this.$route.fullPath);
      if (this.$route.fullPath !== path) {
        this.$router.replace(path);
      }
    }
  }

  private trimLoginInformation(source: string): string {
    if (!source) {
      return source;
    }
    const values = source.split("?");
    if (values.length < 2) {
      return source;
    }
    return values[0] + "?" + Enumerable.from(values[1].split("&")).where(p => !this.isLoginParameter(p)).toArray().join("&");
  }

  private isLoginParameter(parameter: string): boolean {
    const lower = parameter.toLowerCase();
    return lower.startsWith("usercode=")
        || lower.startsWith("userid=")
        || lower.startsWith("departmentcode=")
        || lower.startsWith("departmentid=")
        || lower.startsWith("hostname=")
        || lower.startsWith("disablecloseconfirm=")
        || lower.startsWith("orderusercode=")
        || lower.startsWith("attendingusercode=")
        || lower.startsWith("clientipaddress=");
  }

  get shouldOpenDepartmentButtonSelectorDialog(): boolean {
    return !this.$store.getters.department
        || (this.shouldSelectDepartmentManually && !this.departmentCode);
  }

  async selectLoginDepartment(): Promise<void> {
    if (!this.shouldOpenDepartmentButtonSelectorDialog) { return; }

    const condition = {
      name: "ログイン" + this.$wording.common.department,
      isCancelable: false,
      isUserDepartmentsOnly: true,
    };
    await this.$commonDialogs.department.buttonSelector.selectOptionConditionally(condition, null)
      .then(d => {
        if (d !== undefined) {
          this.$store.commit("setDepartment", d);
        }
        if (d) {
          DomainRegistry.department.getDepartmentRepository().saveSelectedDepartment(this.$store.getters.user.id, d.id);
        }
      })
      .catch(e => {
        this.$error(
          "ログイン" + this.$wording.common.department + "選択失敗",
          "ログイン" + this.$wording.common.department + "選択中にエラーが発生しました。",
          e,
        );
      });
  }

  private handleAuthenticationFailed(error: Error, title: string, message: string): void {
    this.password = "";
    this.$error(
      title,
      message,
      error,
    );
  }

  close(): void {
    this.$closeWindow();
  }

  focusToPassword(): void {
    const passwordElement = this.$refs.password as HTMLInputElement;
    passwordElement.focus();
  }
}
